import {produce} from 'immer';
import React from 'react';
import {assign, setup, fromPromise, assertEvent} from 'xstate';

import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import {TranslationParameters} from 'antibodies/nested-data-creation/constants/AntibodyNestedDataCreationConstants';
import * as AntibodyNestedDataCreationSpreadsheetImportDAO from 'antibodies/nested-data-creation/spreadsheet-import/AntibodyNestedDataCreationSpreadsheetImportDAO';
import {getPriorityForComponentType} from 'antibodies/nested-data-creation/spreadsheet-import/AntibodyNestedDataCreationSpreadsheetImportUtil';
import {
  AntibodyNestedDataCreationSpreadsheetImportState,
  INITIAL_COMPONENT_SCHEMA_CONFIG,
  INITIAL_ANTIBODY_NESTED_DATA_CREATION_SPREADSHEET_IMPORT_STATE,
  NdcSpreadsheetInferredColumnErrorResponse,
  NdcStartingType,
  ComponentSchemaConfig,
  isNdcColumnInferenceError,
  isNdcColumnInferenceResult,
  isNdcColumnInferenceResponse,
  NdcColumnInferenceRequest,
  NdcColumnInferenceResponse,
  NdcSpreadsheetInferredColumnResponse,
  NdcSpreadsheetImportColumnType,
  NdcSpreadsheetImportRequest,
  NdcSpreadsheetImportValidationErrorBadRequest,
  NdcSpreadsheetInterpretation,
} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import {assert} from 'Asserts';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import notifications from 'notifications';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import * as logging from 'util/Logging';
import {getUserMessage, isServerRequestError} from 'util/Net';

async function handleInferColumnsForNestedCreateAndRegisterSpreadsheet(
  input: NdcColumnInferenceRequest
): Promise<NdcColumnInferenceResponse | {errorMessage: string}> {
  try {
    const result =
      await AntibodyNestedDataCreationSpreadsheetImportDAO.inferColumnsForNestedCreateAndRegisterSpreadsheet(
        input
      );
    return result;
  } catch (e) {
    if (isServerRequestError(e)) {
      const userMessage = getUserMessage(e);
      const errorMessage = userMessage ?? 'Failed to infer column types for spreadsheet import';
      return {errorMessage};
    } else {
      logging.error(e, {extra: {message: 'Failed to infer column types for spreadsheet import'}});
      notifications.notifyError(e);
      throw e;
    }
  }
}

async function handleNestedCreateAndRegisterFromSpreadsheetImport(
  input: NdcSpreadsheetImportRequest
): Promise<void | NdcSpreadsheetImportValidationErrorBadRequest> {
  try {
    LoadingScreen.show('Creating antibodies from spreadsheet...');
    const result =
      await AntibodyNestedDataCreationSpreadsheetImportDAO.nestedCreateAndRegisterFromSpreadsheetImport(
        input
      );
    if (result.ndcWorkspaceUrl) {
      notifications.info(
        <>
          <p>We're working on creating antibodies and components...</p>
          <a href={result.ndcWorkspaceUrl}>View progress</a>
        </>
      );
    } else {
      notifications.info("We're working on creating antibodies and components...");
    }
  } catch (e) {
    // Catch BadRequestError and get the validationErrors from it, if they're set
    if (e.status === 400 && e.data?.validationErrors != null) {
      const validationErrors = e.data.validationErrors;
      return {validationErrors};
    } else {
      logging.error(e, {
        extra: {message: 'Failed to create and register antibodies from spreadsheet import'},
      });
      notifications.notifyError(e);
      throw e;
    }
  } finally {
    LoadingScreen.hide();
  }
}

function guardAntibodySchemaAndFormatAndRegistryAreSet(
  context: AntibodyNestedDataCreationSpreadsheetImportState
): boolean {
  return (
    context.selectedFormatId != null && context.selectedSchemaId != null && context.selectedRegistryId != null
  );
}

function guardSpreadsheetSetAndInterpretedWithoutError(
  context: AntibodyNestedDataCreationSpreadsheetImportState
): boolean {
  return (
    context.spreadsheetFile != null &&
    context.spreadsheetURL != null &&
    context.spreadsheetInterpretation != null &&
    context.columnInferenceErrorMessage == null
  );
}

function guardSpreadsheetSetAndNotInferred(
  context: AntibodyNestedDataCreationSpreadsheetImportState
): boolean {
  return (
    context.spreadsheetFile != null &&
    context.spreadsheetURL != null &&
    context.spreadsheetInterpretation == null &&
    context.columnInferenceErrorMessage == null
  );
}

/** Guards against the case where column inference returns an error message for a spreadsheet-wide error, or a
 * response containing columns with no inferred types or error messages. We expect the backend to always
 * return a non-empty inferred types or error message, but we handle it explicitly in case something breaks.
 * */
function guardColumnInferenceSucceededAndIsValid(
  output: NdcColumnInferenceResponse | {errorMessage: string}
): boolean {
  if (!isNdcColumnInferenceResponse(output)) {
    logging.info('Cannot proceed: column inference returned a spreadsheet-wide error');
    return false;
  }

  if (
    output.columnTypes.every((columnType) => {
      if (isNdcColumnInferenceError(columnType) && columnType.errorMessage != null) {
        return true;
      } else if (isNdcColumnInferenceResult(columnType) && columnType.inferredComponentTypes.length > 0) {
        return true;
      } else {
        logging.error('Received column type with no inferred types or error message');
        return false;
      }
    })
  ) {
    return true;
  } else {
    logging.info('Cannot proceed: some columns are missing inferred types or error messages');
    return false;
  }
}

function parseColumnInferenceResponseToSpreadsheetInterpretation(
  columnInferenceResponse: NdcColumnInferenceResponse
): NdcSpreadsheetInterpretation {
  const columns = columnInferenceResponse.columnTypes.map(
    (column: NdcSpreadsheetInferredColumnResponse | NdcSpreadsheetInferredColumnErrorResponse) => {
      if (isNdcColumnInferenceError(column)) {
        return {
          columnName: column.columnName,
          sampleValue: column.sampleValue,
          componentFormatKey: null,
          errorMessage: column.errorMessage,
          inferredTypes: null,
          selectedType: null,
        };
      } else {
        return {
          columnName: column.columnName,
          sampleValue: column.sampleValue,
          componentFormatKey: column.componentFormatKey,
          errorMessage: null,
          inferredTypes: column.inferredComponentTypes,
          // We automatically set the selected type to the only inferred type if there is only
          // one, so that the ColumnMappingPane knows not to display a warning to the user for
          // this column.
          selectedType: column.inferredComponentTypes.length === 1 ? column.inferredComponentTypes[0] : null,
        };
      }
    }
  );

  return {
    columns,
    rowCount: columnInferenceResponse.rowCount,
  };
}

function guardSpreadsheetInterpretationIsValid(
  spreadsheetInterpretation: NdcSpreadsheetInterpretation | null
): boolean {
  if (spreadsheetInterpretation == null) {
    logging.info('Cannot proceed: spreadsheetInterpretation is missing');
    return false;
  } else if (spreadsheetInterpretation.columns.some((column) => column.errorMessage != null)) {
    logging.info('Cannot proceed: some columns have errors');
    return false;
  } else if (
    !spreadsheetInterpretation.columns.every(
      (column) => column.inferredTypes != null && column.inferredTypes.length > 0
    )
  ) {
    logging.info('Cannot proceed: not all columns have at least one inferred type');
    return false;
  }

  return true;
}

function guardComponentSchemaConfigIsValid(componentSchemaConfig: ComponentSchemaConfig): boolean {
  if (componentSchemaConfig.dnaDomainSchemaId == null) {
    logging.info('Cannot proceed: dnaDomainSchemaId is missing');
    return false;
  }
  if (componentSchemaConfig.dnaChainSchemaId == null) {
    logging.info('Cannot proceed: dnaChainSchemaId is missing');
    return false;
  }
  if (componentSchemaConfig.dnaPairSchemaId == null) {
    logging.info('Cannot proceed: dnaPairSchemaId is missing');
    return false;
  }
  if (componentSchemaConfig.aaPairSchemaId == null) {
    logging.info('Cannot proceed: aaPairSchemaId is missing');
    return false;
  }
  return true;
}

function guardSelectedProjectFolderIsValid(selectedProjectFolderId: string | null): boolean {
  return selectedProjectFolderId != null;
}

/** Prepares the NdcSpreadsheetImportRequest for submitting the spreadsheet. Will assert on all required
 * fields being ready, assuming that all of these have been guarded against in earlier state transitions. */
function prepareNdcSpreadsheetImportRequest(
  context: AntibodyNestedDataCreationSpreadsheetImportState
): NdcSpreadsheetImportRequest {
  assert(context.selectedProjectFolderId != null, 'Project folder ID should be set');
  assert(context.selectedSchemaId != null, 'Schema ID should be set');
  assert(context.spreadsheetURL != null, 'Spreadsheet URL should be set');
  assert(context.selectedStartingType != null, 'Starting type should be set');
  assert(context.spreadsheetInterpretation != null, 'Spreadsheet interpretation should be set');

  const columnTypes: Array<NdcSpreadsheetImportColumnType> = context.spreadsheetInterpretation.columns.map(
    (column) => {
      assert(
        column.inferredTypes != null && column.inferredTypes.length > 0,
        'Column should have inferred types'
      );

      // If selectedType is not set, use the 'highest priority' inferred type. See the docstring on
      // NdcColumnState.selectedType for more details.
      const finalType =
        column.selectedType ??
        column.inferredTypes.sort(
          (a, b) => getPriorityForComponentType(a) - getPriorityForComponentType(b)
        )[0];

      return {
        columnName: column.columnName,
        componentFormatKey: column.componentFormatKey!,
        ...finalType,
      };
    }
  );

  return {
    projectFolderId: context.selectedProjectFolderId,
    antibodySchemaId: context.selectedSchemaId,
    spreadsheetLocation: context.spreadsheetURL,
    componentType: context.selectedStartingType,
    columnTypes,
    translationParameters: context.translationParameters,
  };
}

type AntibodyNestedDataCreationSpreadsheetImportModalEvents =
  // Navigation
  | {type: 'next'}
  | {type: 'back'}
  // Select Format pane
  | {type: 'selectFormat'; format: AntibodyFormat | null}
  | {type: 'selectSchema'; schemaId: string | null}
  | {type: 'setRegistryId'; registryId: string | null}
  | {type: 'setStartingType'; startingType: NdcStartingType}
  // Upload Spreadsheet pane
  | {type: 'setSpreadsheetFile'; spreadsheetFile: File}
  | {type: 'setSpreadsheetURL'; spreadsheetURL: string}
  | {type: 'clearSpreadsheet'}
  // Column Mapping pane
  | {type: 'updateSpreadsheetInterpretation'; spreadsheetInterpretation: NdcSpreadsheetInterpretation}
  // Finalize pane
  | {type: 'setTranslationParameters'; translationParameters: TranslationParameters}
  | {type: 'setProjectFolder'; projectFolderId: string}
  | {type: 'setComponentSchemaConfig'; componentSchemaConfig: ComponentSchemaConfig}
  | {type: 'submitSpreadsheetImport'};

/**
 * State machine handling the progress and state of importing antibodies from a spreadsheet in a modal.
 *
 * -----------------------------------------------------------------------------------------------------------
 * | State                | Purpose                                                                          |
 * |----------------------|----------------------------------------------------------------------------------|
 * | selectFormat         | User picks antibody format, schema, registry, starting type (domains/chains/pairs)|
 * | uploadSpreadsheet    | User uploads a spreadsheet file (stored locally + S3 URL), backend infers column |
 * |                      | types from headers and first row and displays errors requiring reupload          |
 * | columnMapping        | User reviews/picks inferred column types for each spreadsheet column when        |
 * |                      | multiple inferred types are present                                              |
 * | finalize             | User picks project folder and component schemas                                  |
 * | saving               | Loading state while backend validates and kicks off the pipeline                 |
 * | validationErrors     | Shows validation errors thrown during validation (e.g. missing entities)         |
 * | done                 | Modal closes since pipeline was successfully kicked off                          |
 * ---------------------------------------------------------------------------------------------------------*/
const AntibodyNestedDataCreationSpreadsheetImportModalMachine = setup({
  types: {
    context: {} as AntibodyNestedDataCreationSpreadsheetImportState,
    events: {} as AntibodyNestedDataCreationSpreadsheetImportModalEvents,
    input: {} as {initialProjectFolderId?: string | null},
  },
  actors: {
    inferColumnTypes: fromPromise(
      async ({
        input,
      }: {
        input: NdcColumnInferenceRequest;
      }): Promise<NdcColumnInferenceResponse | {errorMessage: string}> =>
        await handleInferColumnsForNestedCreateAndRegisterSpreadsheet(input)
    ),
    submitSpreadsheetImport: fromPromise(
      async ({input}: {input: NdcSpreadsheetImportRequest}) =>
        await handleNestedCreateAndRegisterFromSpreadsheetImport(input)
    ),
  },
  actions: {
    // Select Format pane
    selectFormat: assign(({context, event}) => {
      assertEvent(event, 'selectFormat');
      const formatId = event.format?.id ?? null;
      return produce(context, (draft) => {
        draft.selectedFormatId = formatId;
      });
    }),
    selectSchema: assign(({context, event}) => {
      assertEvent(event, 'selectSchema');
      if (context.selectedSchemaId === event.schemaId) {
        return context;
      }
      return produce(context, (draft) => {
        draft.selectedSchemaId = event.schemaId;
        draft.spreadsheetInterpretation = null;
        draft.componentSchemaConfig = {...INITIAL_COMPONENT_SCHEMA_CONFIG};
      });
    }),
    setRegistryId: assign(({context, event}) => {
      assertEvent(event, 'setRegistryId');
      return produce(context, (draft) => {
        draft.selectedRegistryId = event.registryId;
      });
    }),
    setStartingType: assign(({context, event}) => {
      assertEvent(event, 'setStartingType');
      if (context.selectedStartingType === event.startingType) {
        return context;
      }
      return produce(context, (draft) => {
        draft.selectedStartingType = event.startingType;
        draft.spreadsheetInterpretation = null;
        draft.componentSchemaConfig = {...INITIAL_COMPONENT_SCHEMA_CONFIG};
      });
    }),
    // Upload Spreadsheet pane
    setSpreadsheetFile: assign(({context, event}) => {
      assertEvent(event, 'setSpreadsheetFile');
      return produce(context, (draft) => {
        draft.spreadsheetFile = event.spreadsheetFile;
        draft.spreadsheetInterpretation = null;
        draft.columnInferenceErrorMessage = null;
      });
    }),
    setSpreadsheetURL: assign(({context, event}) => {
      assertEvent(event, 'setSpreadsheetURL');
      return produce(context, (draft) => {
        draft.spreadsheetURL = event.spreadsheetURL;
        draft.spreadsheetInterpretation = null;
        draft.columnInferenceErrorMessage = null;
      });
    }),
    clearSpreadsheet: assign(({context, event}) => {
      assertEvent(event, 'clearSpreadsheet');
      return produce(context, (draft) => {
        draft.spreadsheetFile = null;
        draft.spreadsheetURL = null;
        draft.spreadsheetInterpretation = null;
        draft.columnInferenceErrorMessage = null;
      });
    }),
    // Column Mapping pane
    updateSpreadsheetInterpretation: assign(({context, event}) => {
      assertEvent(event, 'updateSpreadsheetInterpretation');
      return produce(context, (draft) => {
        draft.spreadsheetInterpretation = event.spreadsheetInterpretation;
      });
    }),
    // Finalize pane
    setTranslationParameters: assign(({context, event}) => {
      assertEvent(event, 'setTranslationParameters');
      return produce(context, (draft) => {
        draft.translationParameters = event.translationParameters;
      });
    }),
    setProjectFolder: assign(({context, event}) => {
      assertEvent(event, 'setProjectFolder');
      return produce(context, (draft) => {
        draft.selectedProjectFolderId = event.projectFolderId;
      });
    }),
    setComponentSchemaConfig: assign(({context, event}) => {
      assertEvent(event, 'setComponentSchemaConfig');
      return produce(context, (draft) => {
        draft.componentSchemaConfig = event.componentSchemaConfig;
      });
    }),
  },
}).createMachine({
  context: ({input}) =>
    produce(INITIAL_ANTIBODY_NESTED_DATA_CREATION_SPREADSHEET_IMPORT_STATE, (draft) => {
      draft.selectedProjectFolderId =
        input.initialProjectFolderId ?? FolderDefaults.get(FolderDefaultsKey.INVENTORY) ?? null;
    }),
  id: 'antibodyNestedDataCreationSpreadsheetImportModal',
  initial: 'selectFormat',
  states: {
    selectFormat: {
      on: {
        next: {
          target: 'uploadSpreadsheet',
          guard: ({context}) => guardAntibodySchemaAndFormatAndRegistryAreSet(context),
        },
        selectFormat: {actions: 'selectFormat'},
        selectSchema: {actions: 'selectSchema'},
        setRegistryId: {actions: 'setRegistryId'},
        setStartingType: {actions: 'setStartingType'},
      },
    },
    uploadSpreadsheet: {
      initial: 'selectingSpreadsheet',
      on: {
        next: {
          target: 'columnMapping',
          guard: ({context}) => guardSpreadsheetSetAndInterpretedWithoutError(context),
        },
        back: {
          target: 'selectFormat',
        },
      },
      states: {
        selectingSpreadsheet: {
          always: {
            target: 'inferringColumns',
            guard: ({context}) => guardSpreadsheetSetAndNotInferred(context),
          },
          on: {
            setSpreadsheetFile: {actions: 'setSpreadsheetFile'},
            setSpreadsheetURL: {actions: 'setSpreadsheetURL'},
            clearSpreadsheet: {actions: 'clearSpreadsheet'},
          },
        },
        inferringColumns: {
          invoke: {
            src: 'inferColumnTypes',
            input: ({
              context,
            }: {
              context: AntibodyNestedDataCreationSpreadsheetImportState;
            }): NdcColumnInferenceRequest => {
              assert(context.spreadsheetURL != null, 'Spreadsheet URL should be set');
              assert(context.selectedSchemaId != null, 'Schema ID should be set');
              return {
                spreadsheetLocation: context.spreadsheetURL,
                antibodySchemaId: context.selectedSchemaId,
                componentFormatType: context.selectedStartingType,
              };
            },
            onDone: [
              {
                target: 'columnsInferred',
                guard: ({event}) => guardColumnInferenceSucceededAndIsValid(event.output),
                actions: assign(({context, event}) => {
                  const columnInferenceResponse = event.output;
                  assert(
                    isNdcColumnInferenceResponse(columnInferenceResponse),
                    'Action guard should prevent action from running if column inference returned an error'
                  );
                  return produce(context, (draft) => {
                    draft.spreadsheetInterpretation =
                      parseColumnInferenceResponseToSpreadsheetInterpretation(columnInferenceResponse);
                  });
                }),
              },
              {
                target: 'showingError',
                guard: ({event}) => !guardColumnInferenceSucceededAndIsValid(event.output),
                actions: assign(({context, event}) => {
                  const errorMessage = !isNdcColumnInferenceResponse(event.output)
                    ? event.output.errorMessage
                    : // If the column inference response is not a valid one. See docstring on
                      // guardColumnInferenceSucceededAndIsValid for more details.
                      'An unexpected error occurred while processing your spreadsheet.';

                  return produce(context, (draft) => {
                    draft.columnInferenceErrorMessage = errorMessage;
                  });
                }),
              },
            ],
            onError: {
              target: 'showingError',
            },
          },
          on: {
            clearSpreadsheet: {
              target: 'selectingSpreadsheet',
              actions: 'clearSpreadsheet',
            },
          },
        },
        columnsInferred: {
          on: {
            clearSpreadsheet: {
              target: 'selectingSpreadsheet',
              actions: 'clearSpreadsheet',
            },
          },
        },
        showingError: {
          on: {
            clearSpreadsheet: {
              target: 'selectingSpreadsheet',
              actions: 'clearSpreadsheet',
            },
          },
        },
      },
    },
    columnMapping: {
      on: {
        next: {
          target: 'finalize',
          guard: ({context}) => guardSpreadsheetInterpretationIsValid(context.spreadsheetInterpretation),
        },
        back: {
          target: 'uploadSpreadsheet',
        },
        updateSpreadsheetInterpretation: {
          actions: 'updateSpreadsheetInterpretation',
        },
      },
    },
    finalize: {
      on: {
        back: {
          target: 'columnMapping',
        },
        submitSpreadsheetImport: {
          target: 'saving',
          guard: ({context}) =>
            guardComponentSchemaConfigIsValid(context.componentSchemaConfig) &&
            guardSelectedProjectFolderIsValid(context.selectedProjectFolderId),
        },
        setTranslationParameters: {actions: 'setTranslationParameters'},
        setProjectFolder: {actions: 'setProjectFolder'},
        setComponentSchemaConfig: {actions: 'setComponentSchemaConfig'},
      },
    },
    saving: {
      invoke: {
        src: 'submitSpreadsheetImport',
        input: ({
          context,
        }: {
          context: AntibodyNestedDataCreationSpreadsheetImportState;
        }): NdcSpreadsheetImportRequest => prepareNdcSpreadsheetImportRequest(context),
        onDone: [
          {
            target: 'validationErrors',
            guard: ({event}) =>
              event.output?.validationErrors != null && event.output.validationErrors.length > 0,
          },
          {
            target: 'done',
            guard: ({event}) =>
              event.output?.validationErrors == null || event.output.validationErrors.length === 0,
          },
        ],
        onError: {
          target: 'finalize',
        },
      },
    },
    validationErrors: {
      on: {
        back: {
          target: 'finalize',
        },
      },
    },
    done: {
      type: 'final',
    },
  },
});

export default AntibodyNestedDataCreationSpreadsheetImportModalMachine;
